import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, BarChart3, Play, Plus, Save, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { SkeletonCard } from "@/components/skeleton";

type SurveyQuestionType = "single_choice" | "multiple_choice" | "rating" | "open_text";
type SurveyDisplayAt = "pre" | "during" | "post";
type SurveyStatus = "draft" | "active" | "closed" | "archived";
type SurveyType = "quick_poll" | "nps" | "multi_question" | "rating_matrix" | "open_ended" | "prioritization";

interface SurveyOption {
  id: string;
  label: string;
}

interface SurveyQuestion {
  id: string;
  type: SurveyQuestionType;
  prompt: string;
  required?: boolean;
  options?: SurveyOption[];
}

interface WebinarSurvey {
  id: string;
  title: string;
  surveyType: SurveyType;
  displayAt: SurveyDisplayAt;
  questions: SurveyQuestion[];
  status: SurveyStatus;
  sortOrder: number;
  launchedAt: string | null;
  closedAt: string | null;
}

interface SurveyResultQuestion {
  questionId: string;
  prompt: string;
  type: SurveyQuestionType;
  counts?: Array<{ optionId: string; label: string; responses: number }>;
  average?: number | null;
  responses?: number | string[];
}

interface SurveyResults {
  surveyId: string;
  totalResponses: number;
  questions: SurveyResultQuestion[];
}

interface DraftSurvey {
  id?: string;
  title: string;
  surveyType: SurveyType;
  displayAt: SurveyDisplayAt;
  sortOrder: number;
  questions: SurveyQuestion[];
}

function createQuestion(questionType: SurveyQuestionType = "single_choice"): SurveyQuestion {
  return {
    id: crypto.randomUUID(),
    type: questionType,
    prompt: "",
    required: false,
    options: questionType === "single_choice" || questionType === "multiple_choice"
      ? [
          { id: crypto.randomUUID(), label: "Option 1" },
          { id: crypto.randomUUID(), label: "Option 2" },
        ]
      : undefined,
  };
}

function createDraft(): DraftSurvey {
  return {
    title: "",
    surveyType: "multi_question",
    displayAt: "post",
    sortOrder: 0,
    questions: [createQuestion()],
  };
}

function badgeClass(status: SurveyStatus) {
  switch (status) {
    case "active":
      return "bg-[var(--color-success)]/10 text-[var(--color-success)]";
    case "closed":
      return "bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
    case "archived":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-primary/10 text-primary";
  }
}

export default function WebinarSurveysPage() {
  const { webinarId } = useParams();
  const [surveys, setSurveys] = useState<WebinarSurvey[]>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, SurveyResults | undefined>>({});
  const [draft, setDraft] = useState<DraftSurvey>(createDraft());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedSurvey = useMemo(
    () => surveys.find((survey) => survey.id === selectedSurveyId) ?? null,
    [selectedSurveyId, surveys],
  );

  const loadSurveys = useCallback(async () => {
    if (!webinarId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiFetch<{ data: WebinarSurvey[] }>(`/v1/webinars/${webinarId}/surveys`);
      setSurveys(response.data);
      if (!selectedSurveyId && response.data[0]?.id) {
        setSelectedSurveyId(response.data[0].id);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load webinar surveys.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedSurveyId, webinarId]);

  useEffect(() => {
    void loadSurveys();
  }, [loadSurveys]);

  useEffect(() => {
    if (!selectedSurvey) {
      return;
    }

    setDraft({
      id: selectedSurvey.id,
      title: selectedSurvey.title,
      surveyType: selectedSurvey.surveyType,
      displayAt: selectedSurvey.displayAt,
      sortOrder: selectedSurvey.sortOrder,
      questions: selectedSurvey.questions,
    });
  }, [selectedSurvey]);

  async function loadResults(surveyId: string) {
    if (!webinarId) return;
    const response = await apiFetch<{ data: SurveyResults }>(`/v1/webinars/${webinarId}/surveys/${surveyId}/results`);
    setResults((current) => ({ ...current, [surveyId]: response.data }));
  }

  async function handleSave() {
    if (!webinarId) return;
    if (!draft.title.trim()) {
      setError("Survey title is required.");
      return;
    }
    if (draft.questions.some((question) => !question.prompt.trim())) {
      setError("Each survey question needs a prompt.");
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const body = JSON.stringify({
        title: draft.title,
        surveyType: draft.surveyType,
        displayAt: draft.displayAt,
        sortOrder: draft.sortOrder,
        questions: draft.questions,
      });

      if (draft.id) {
        await apiFetch(`/v1/webinars/${webinarId}/surveys/${draft.id}`, {
          method: "PATCH",
          body,
        });
      } else {
        const response = await apiFetch<{ data: { id: string } }>(`/v1/webinars/${webinarId}/surveys`, {
          method: "POST",
          body,
        });
        setSelectedSurveyId(response.data.id);
      }

      await loadSurveys();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Could not save webinar survey.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleStatusAction(surveyId: string, action: "launch" | "close" | "delete") {
    if (!webinarId) return;
    try {
      setError(null);
      if (action === "delete") {
        await apiFetch(`/v1/webinars/${webinarId}/surveys/${surveyId}`, { method: "DELETE" });
      } else {
        await apiFetch(`/v1/webinars/${webinarId}/surveys/${surveyId}/${action}`, { method: "POST" });
      }
      await loadSurveys();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Could not update survey status.");
    }
  }

  function updateQuestion(questionId: string, updater: (question: SurveyQuestion) => SurveyQuestion) {
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((question) => question.id === questionId ? updater(question) : question),
    }));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/webinars" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            Back to webinars
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-foreground">Webinar Surveys</h1>
          <p className="text-sm text-muted-foreground">
            Configure pre-, during-, and post-webinar surveys on the mounted webinar survey contract.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setSelectedSurveyId(null);
            setDraft(createDraft());
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
        >
          <Plus className="h-4 w-4" />
          New survey
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <section className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">Existing surveys</h2>
              <span className="text-xs text-muted-foreground">{surveys.length} total</span>
            </div>

            {isLoading ? (
              <div className="space-y-3">
                <SkeletonCard className="p-4" />
                <SkeletonCard className="p-4" />
              </div>
            ) : surveys.length === 0 ? (
              <p className="text-sm text-muted-foreground">No webinar surveys configured yet.</p>
            ) : (
              <div className="space-y-3">
                {surveys.map((survey) => (
                  <button
                    key={survey.id}
                    type="button"
                    onClick={() => setSelectedSurveyId(survey.id)}
                    className={`w-full rounded-lg border p-4 text-left transition-colors ${selectedSurveyId === survey.id ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-accent"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{survey.title}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {survey.displayAt} webinar • {survey.questions.length} questions
                        </p>
                      </div>
                      <span className={`rounded-full px-2 py-1 text-[11px] font-medium ${badgeClass(survey.status)}`}>
                        {survey.status}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedSurvey ? (
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">Results</h2>
                <button
                  type="button"
                  onClick={() => void loadResults(selectedSurvey.id)}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs text-foreground hover:bg-accent"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  Refresh
                </button>
              </div>

              {results[selectedSurvey.id] ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    {results[selectedSurvey.id]?.totalResponses ?? 0} responses collected
                  </p>
                  {results[selectedSurvey.id]?.questions.map((question) => (
                    <div key={question.questionId} className="rounded-lg border border-border bg-background p-3">
                      <p className="text-sm font-medium text-foreground">{question.prompt}</p>
                      {question.counts ? (
                        <div className="mt-2 space-y-2">
                          {question.counts.map((count) => (
                            <div key={count.optionId} className="text-xs text-muted-foreground">
                              {count.label}: {count.responses}
                            </div>
                          ))}
                        </div>
                      ) : question.type === "rating" ? (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Average rating: {question.average?.toFixed(1) ?? "Unavailable"}
                        </p>
                      ) : (
                        <p className="mt-2 text-xs text-muted-foreground">
                          {Array.isArray(question.responses) ? `${question.responses.length} free-text responses sampled` : `${question.responses ?? 0} responses`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select refresh to load aggregated results for the selected survey.</p>
              )}
            </div>
          ) : null}
        </section>

        <section className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{draft.id ? "Edit survey" : "Create survey"}</h2>
              <p className="text-sm text-muted-foreground">This surface writes directly to the mounted webinar survey contract.</p>
            </div>
            {draft.id ? (
              <div className="flex gap-2">
                {selectedSurvey?.status === "draft" ? (
                  <button
                    type="button"
                    onClick={() => void handleStatusAction(draft.id!, "launch")}
                    className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-accent"
                  >
                    <Play className="h-4 w-4" />
                    Launch
                  </button>
                ) : null}
                {selectedSurvey?.status === "active" ? (
                  <button
                    type="button"
                    onClick={() => void handleStatusAction(draft.id!, "close")}
                    className="rounded-lg border border-border px-3 py-2 text-sm text-foreground hover:bg-accent"
                  >
                    Close
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => void handleStatusAction(draft.id!, "delete")}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 px-3 py-2 text-sm text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/5"
                >
                  <Trash2 className="h-4 w-4" />
                  Archive
                </button>
              </div>
            ) : null}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span className="font-medium text-foreground">Title</span>
              <input
                value={draft.title}
                onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
                placeholder="Post-webinar feedback"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-foreground">Display stage</span>
              <select
                value={draft.displayAt}
                onChange={(event) => setDraft((current) => ({ ...current, displayAt: event.target.value as SurveyDisplayAt }))}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
              >
                <option value="pre">Pre webinar</option>
                <option value="during">During webinar</option>
                <option value="post">Post webinar</option>
              </select>
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-foreground">Survey type</span>
              <select
                value={draft.surveyType}
                onChange={(event) => setDraft((current) => ({ ...current, surveyType: event.target.value as SurveyType }))}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
              >
                <option value="quick_poll">Quick poll</option>
                <option value="nps">NPS</option>
                <option value="multi_question">Multi-question</option>
                <option value="rating_matrix">Rating matrix</option>
                <option value="open_ended">Open ended</option>
                <option value="prioritization">Prioritization</option>
              </select>
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-foreground">Sort order</span>
              <input
                type="number"
                min={0}
                max={999}
                value={draft.sortOrder}
                onChange={(event) => setDraft((current) => ({ ...current, sortOrder: Number(event.target.value) || 0 }))}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
              />
            </label>
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Questions</h3>
              <button
                type="button"
                onClick={() => setDraft((current) => ({ ...current, questions: [...current.questions, createQuestion()] }))}
                className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs text-foreground hover:bg-accent"
              >
                <Plus className="h-3.5 w-3.5" />
                Add question
              </button>
            </div>

            {draft.questions.map((question, questionIndex) => (
              <div key={question.id} className="rounded-xl border border-border bg-background p-4">
                <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-foreground">Prompt</span>
                    <input
                      value={question.prompt}
                      onChange={(event) => updateQuestion(question.id, (current) => ({ ...current, prompt: event.target.value }))}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
                      placeholder={`Question ${questionIndex + 1}`}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-foreground">Type</span>
                    <select
                      value={question.type}
                      onChange={(event) => updateQuestion(question.id, (current) => {
                        const nextType = event.target.value as SurveyQuestionType;
                        return {
                          ...current,
                          type: nextType,
                          options: nextType === "single_choice" || nextType === "multiple_choice"
                            ? current.options ?? [
                                { id: crypto.randomUUID(), label: "Option 1" },
                                { id: crypto.randomUUID(), label: "Option 2" },
                              ]
                            : undefined,
                        };
                      })}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
                    >
                      <option value="single_choice">Single choice</option>
                      <option value="multiple_choice">Multiple choice</option>
                      <option value="rating">Rating</option>
                      <option value="open_text">Open text</option>
                    </select>
                  </label>
                </div>

                <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={question.required === true}
                    onChange={(event) => updateQuestion(question.id, (current) => ({ ...current, required: event.target.checked }))}
                  />
                  Required
                </label>

                {(question.type === "single_choice" || question.type === "multiple_choice") && question.options ? (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Options</p>
                    {question.options.map((option, optionIndex) => (
                      <div key={option.id} className="flex items-center gap-2">
                        <input
                          value={option.label}
                          onChange={(event) => updateQuestion(question.id, (current) => ({
                            ...current,
                            options: (current.options ?? []).map((entry) => entry.id === option.id ? { ...entry, label: event.target.value } : entry),
                          }))}
                          className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
                          placeholder={`Option ${optionIndex + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => updateQuestion(question.id, (current) => ({
                            ...current,
                            options: (current.options ?? []).filter((entry) => entry.id !== option.id),
                          }))}
                          className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => updateQuestion(question.id, (current) => ({
                        ...current,
                        options: [...(current.options ?? []), { id: crypto.randomUUID(), label: `Option ${(current.options?.length ?? 0) + 1}` }],
                      }))}
                      className="rounded-lg border border-border px-3 py-2 text-xs text-foreground hover:bg-accent"
                    >
                      Add option
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Saving..." : "Save survey"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
